import ThronVideo from '@components/utils/ThronVideo';
import useMediaQueries from '@stone/hooks/useMediaQueries';
import Icon from '@stone/ui/icon/Icon';
import Image from '@stone/ui/image/Image';
import classNames from 'classnames';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SwiperCore, { Navigation, A11y, Keyboard, Pagination, Autoplay } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useElementSize } from 'usehooks-ts';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ReactHtmlParser from 'react-html-parser';
import analytics from '@stone/utils/analytics';
import useIntersectionObserver from '@common/utils/useIntersectionObserver';
import CarouselNavigation from '@stone/ui/button/CarouselNavigation';
import { getFirstFocusable } from '@common/scripts/utilities/focusableElementUtils';
import whatInput from 'what-input';
SwiperCore.use([Keyboard, Navigation, A11y, Pagination, Autoplay]);
const CarouselHero = (props) => {
    const { t } = useTranslation();
    const isReduced = useReducedMotion();
    const { isMobile, isWideDesktop } = useMediaQueries();
    const timing = parseInt(props.autoplay) || 5;
    // ANCHOR - USE STATES ----------------------------------------------------------------------------------------------------------------------------------------
    const [activeData, setActiveData] = useState(null);
    const [playState, setPlayState] = useState();
    const [pushedAnalytics, setPushedAnalytics] = useState([]);
    const [autoplaying, setAutoplaying] = useState(true);
    const [remainingTime, setRemainingTime] = useState(timing * 1000);
    const [clickFromButton, setClickFromButton] = useState(false);
    // ANCHOR - USE REF ----------------------------------------------------------------------------------------------------------------------------------------
    const firstRender = useRef(true);
    const { ref, isIntersecting } = useIntersectionObserver(0.6);
    const mediaRef = useRef(null);
    const swiperRef = useRef(null);
    const intervalRef = useRef(null);
    const [containerRef, { width }] = useElementSize();
    // ANCHOR - USE MEMO ----------------------------------------------------------------------------------------------------------------------------------------
    const slidesArray = useMemo(() => JSON.parse(props.slides), [props.slides]);
    const isMediaPresent = useMemo(() => activeData && (activeData.image || activeData.imageMobile || activeData.thronID || activeData.thronIDMobile), [activeData]);
    const spacing = useMemo(() => {
        const container = width;
        const inners = 12 * 23;
        const col = (container - inners) / 24;
        if (col > 0 && !isMobile) {
            return col + 12 - 26.05;
        }
        return 12;
    }, [width, isMobile]);
    const transform = (node) => {
        var _a;
        if ((_a = node.attribs) === null || _a === void 0 ? void 0 : _a.style) {
            node.attribs.style = null;
        }
    };
    const parseOptions = {
        decodeEntities: true,
        transform,
    };
    const parseColor = (str) => {
        const base = '#000000';
        if (str != null) {
            let regex = new RegExp(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/);
            let color;
            if (str[0] != '#' && str.length >= 3) {
                color = '#' + str;
            }
            else {
                color = str;
            }
            if (regex.test(color)) {
                return color;
            }
            return base;
        }
        return base;
    };
    const parseTransparency = (str) => {
        const base = 55;
        let regex = new RegExp(/^\d+$/);
        if (str != null && regex.test(str)) {
            let transparency = parseInt(str);
            if (transparency >= 0 && transparency <= 100) {
                return transparency;
            }
            else if (transparency < 0) {
                return 0;
            }
            else if (transparency > 100) {
                return 100;
            }
            return base;
        }
        return base;
    };
    // ANCHOR - USE CALLABCKS ----------------------------------------------------------------------------------------------------------------------------------------
    const isActive = useCallback((slide) => (activeData === null || activeData === void 0 ? void 0 : activeData.ID) === slide.ID, [activeData]);
    const handlePlay = useCallback((index) => {
        const _array = Object.assign({}, playState);
        _array[index] = !playState[index];
        setPlayState(_array);
    }, [playState]);
    const onSlideChange = useCallback((index) => {
        if (slidesArray.length) {
            const activeSlide = slidesArray[index];
            setActiveData(Object.assign(Object.assign({}, activeSlide), { index: index }));
        }
    }, [slidesArray, isReduced, pushedAnalytics]);
    const scrollParallax = useCallback(() => {
        const tl = gsap.timeline();
        tl.to(ref.current.parentNode, {
            opacity: 0,
        });
        ScrollTrigger.create({
            id: 'prlx-desktop',
            trigger: ref.current.parentNode,
            start: () => `bottom bottom`,
            end: 'bottom top',
            pinSpacing: false,
            pin: true,
            // markers: true,
            animation: tl,
            scrub: true,
        });
    }, []);
    const swiperProps = {
        loop: true,
        watchOverflow: true,
        slidesPerView: 'auto',
    };
    const TIMER_MULTIPLIER = 200;
    function startTimer() {
        intervalRef.current = setInterval(() => {
            setRemainingTime((prev) => {
                const time = Math.min(timing * 1000, Math.max(0, prev - TIMER_MULTIPLIER));
                return time;
            });
        }, TIMER_MULTIPLIER);
    }
    function pauseTimer() {
        var _a, _b;
        clearInterval(intervalRef.current);
        //@ts-ignore disable-next-line
        (_b = (_a = swiperRef.current) === null || _a === void 0 ? void 0 : _a.params) === null || _b === void 0 ? void 0 : _b.autoplay.delay = remainingTime;
    }
    function resetTimer() {
        setRemainingTime(timing * 1000);
    }
    function startAutoplay() {
        // customEventBus.emit(`playerstatuschange:${props.id}`, { id: props.id, status: 'pause' });
        swiperRef.current.autoplay.run();
        setAutoplaying(true);
        startTimer();
    }
    function pauseAutoPlay() {
        swiperRef.current.autoplay.pause();
        swiperRef.current.animating = false;
        // customEventBus.emit(`playerstatuschange:${props.id}`, { id: props.id, status: 'play' });
        setAutoplaying(false);
        pauseTimer();
        //@ts-ignore disable-next-line
        // swiperRef.current.params.autoplay.delay = remainingTime;
    }
    // ANCHOR - USE EFFECTS ----------------------------------------------------------------------------------------------------------------------------------------
    useEffect(() => {
        if (firstRender.current && slidesArray.length) {
            setActiveData(Object.assign(Object.assign({}, slidesArray[0]), { index: 0 }));
            firstRender.current = false;
        }
    }, [slidesArray]);
    useEffect(() => {
        document.documentElement.style.setProperty('--carousel-hero-autoplay', timing.toString() + 's');
        startTimer();
    }, []);
    useEffect(() => {
        if (!!(slidesArray === null || slidesArray === void 0 ? void 0 : slidesArray.length)) {
            setPlayState(() => slidesArray === null || slidesArray === void 0 ? void 0 : slidesArray.map(() => !isReduced));
        }
    }, [slidesArray]);
    useEffect(() => {
        if (activeData && !pushedAnalytics.includes(activeData === null || activeData === void 0 ? void 0 : activeData.ID) && isIntersecting) {
            const prev = pushedAnalytics;
            prev.push(activeData.ID);
            analytics.promotion('view_promotion', activeData.index + 1, activeData.title_analytics || activeData.title);
            setPushedAnalytics(prev);
        }
    }, [activeData, pushedAnalytics, isIntersecting]);
    useLayoutEffect(() => {
        if (!isReduced) {
            if (ref.current && props.parallax) {
                scrollParallax();
            }
        }
        return () => ScrollTrigger.getAll().forEach((el) => el.kill());
    }, [isReduced]);
    useLayoutEffect(() => {
        if ((mediaRef === null || mediaRef === void 0 ? void 0 : mediaRef.current) && activeData) {
            const color = parseColor(activeData === null || activeData === void 0 ? void 0 : activeData.overlay_color);
            const transparency = parseTransparency(activeData === null || activeData === void 0 ? void 0 : activeData.overlay_transparency);
            mediaRef.current.style.setProperty('--overlay-color', color);
            mediaRef.current.style.setProperty('--overlay-transparency', (transparency / 100).toString());
        }
    }, [activeData]);
    // ANCHOR - COMPONENTS ----------------------------------------------------------------------------------------------------------------------------------------
    const renderMedia = () => {
        var _a, _b;
        return isMediaPresent && (React.createElement(React.Fragment, null,
            React.createElement(AnimatePresence, null,
                React.createElement(motion.div, { ref: mediaRef, className: "carousel-hero__media", key: activeData.ID, initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: {
                        duration: 1,
                    } }, activeData.thronID || activeData.thronIDMobile ? (React.createElement("div", { className: "video-container" },
                    React.createElement(ThronVideo, { autoplay: playState[activeData.index], thronID: activeData.thronID, thronMobileID: activeData.thronIDMobile, lockBitrate: activeData === null || activeData === void 0 ? void 0 : activeData.maxBitrate }),
                    React.createElement("div", { className: "btn--container container" },
                        React.createElement("button", { className: "btn--pause button button--quaternary inverted", "aria-label": t('button.playpausevideoA11y'), "data-pause": playState[activeData.index], "aria-pressed": !playState[activeData.index], onClick: () => handlePlay(activeData.index) },
                            React.createElement(Icon, { family: "16", name: playState[activeData.index] ? 'pause' : 'play', inverted: true, autoSize: true }),
                            React.createElement("span", null, playState[activeData.index] ? t('button.pause') : t('button.play')))))) : ((activeData.image || activeData.imageMobile) && React.createElement(Image, { src: (_a = activeData === null || activeData === void 0 ? void 0 : activeData.image) === null || _a === void 0 ? void 0 : _a.path, srcMobile: (_b = activeData === null || activeData === void 0 ? void 0 : activeData.imageMobile) === null || _b === void 0 ? void 0 : _b.path, alt: '' }))))));
    };
    const renderPagination = () => {
        var _a;
        return (React.createElement("div", { className: "pagination--container container" },
            React.createElement("div", { className: "row" },
                React.createElement("div", { className: "col-lg-4 col-xxl-3 offset-lg-2 carousel-hero__controls" },
                    React.createElement("div", { className: "wrapper" },
                        ((_a = swiperRef.current) === null || _a === void 0 ? void 0 : _a.autoplay) && (React.createElement("button", { className: "button button--pause inverted", "aria-label": t('carousel.autoplay.pause'), "aria-pressed": autoplaying, onClick: () => {
                                if (autoplaying) {
                                    pauseAutoPlay();
                                }
                                else {
                                    startAutoplay();
                                }
                            } },
                            React.createElement(Icon, { family: "16", name: autoplaying ? 'pause' : 'play', inverted: true, autoSize: true }))),
                        React.createElement(CarouselNavigation, { id: "hero-carousel", inverted: true })))),
            React.createElement("div", { className: "row" },
                React.createElement("div", { className: classNames('col-lg-4 col-xxl-3 offset-lg-2 pagination carousel-hero__pagination', { paused: !autoplaying }) }))));
    };
    const renderSlide = (slide, i, isFocusable) => {
        const slideRef = useRef(null);
        useEffect(() => {
            if (slideRef.current) {
                slideRef.current.parentElement.ariaHidden = !isFocusable ? 'true' : 'false';
            }
        }, [isFocusable]);
        return (React.createElement("div", { ref: slideRef, className: classNames('carousel-hero__slide', { active: isActive(slide) }), "aria-hidden": !isFocusable },
            React.createElement("p", { className: "carousel-hero__slide-index font__title" }, (slidesArray === null || slidesArray === void 0 ? void 0 : slidesArray.length) > 1 && (i + 1).toString().padStart(3, '0')),
            React.createElement("div", { className: "carousel-hero__slide-content" },
                React.createElement("h2", { className: "carousel-hero__slide-title font__title" }, slide.title),
                React.createElement("div", { className: "carousel-hero__slide-description" }, ReactHtmlParser(slide.description, parseOptions))),
            React.createElement("div", { className: "carousel-hero__slide-links" },
                (slide === null || slide === void 0 ? void 0 : slide.first_link_url) && (slide === null || slide === void 0 ? void 0 : slide.first_link_label) && (React.createElement("a", { href: slide === null || slide === void 0 ? void 0 : slide.first_link_url, className: "button button--tertiary inverted", tabIndex: isFocusable ? 0 : -1, onClick: () => analytics.promotion('select_promotion', i + 1, slide.first_link_analytics || slide.first_link_label) },
                    React.createElement("span", null, slide === null || slide === void 0 ? void 0 : slide.first_link_label))),
                (slide === null || slide === void 0 ? void 0 : slide.second_link_url) && (slide === null || slide === void 0 ? void 0 : slide.second_link_label) && (React.createElement("a", { href: slide === null || slide === void 0 ? void 0 : slide.second_link_url, className: "button button--tertiary inverted", tabIndex: isFocusable ? 0 : -1, onClick: () => analytics.promotion('select_promotion', i + 1, (slide === null || slide === void 0 ? void 0 : slide.second_link_analytics) || (slide === null || slide === void 0 ? void 0 : slide.second_link_label)) },
                    React.createElement("span", null, slide === null || slide === void 0 ? void 0 : slide.second_link_label))))));
    };
    const renderCarousel = () => {
        return (React.createElement(React.Fragment, null,
            React.createElement(Swiper, Object.assign({ onSwiper: (swiper) => (swiperRef.current = swiper), onSlideChange: (swiper) => {
                    onSlideChange(swiper.realIndex);
                    // @ts-ignore disable-next-line
                    swiper.params.autoplay.delay = timing * 1000;
                    resetTimer();
                    if (clickFromButton) {
                        setTimeout(() => getFirstFocusable(swiper.slides[swiper.realIndex]).focus(), 500);
                        setClickFromButton(false);
                    }
                }, onPaginationRender: (swiper, paginationEl) => {
                    const buttons = paginationEl.querySelectorAll('.swiper-pagination-bullet');
                    buttons.forEach((button, i) => {
                        button.addEventListener('click', () => {
                            if (whatInput.ask() === 'keyboard') {
                                setClickFromButton(true);
                            }
                        });
                    });
                }, autoplay: {
                    delay: timing * 1000,
                    disableOnInteraction: false,
                }, pagination: {
                    el: '.carousel-hero__pagination',
                    clickable: true,
                    type: 'bullets',
                    renderBullet: function (index, className) {
                        const paginationMessage = t('carousel.paginationbulletmessage').replace('INDEX_NUMBER', `${index + 1}`);
                        return `<button aria-label="${paginationMessage}" class="${className}"><span class="progress"></span></button>`;
                    },
                }, watchSlidesProgress: true, spaceBetween: spacing, navigation: {
                    nextEl: `.hero-carousel-navigation--next`,
                    prevEl: `.hero-carousel-navigation--prev`,
                } }, swiperProps), slidesArray.length &&
                slidesArray.map((slide, i) => React.createElement(SwiperSlide, { key: `carousel-slide-${i}` }, ({ isDuplicate, isActive }) => renderSlide(slide, i, !isDuplicate && isActive))))));
    };
    return (React.createElement(React.Fragment, null,
        renderMedia(),
        React.createElement("div", { className: "carousel-hero__content--wrapper", ref: ref },
            React.createElement("div", { className: "carousel-hero__content" }, (slidesArray === null || slidesArray === void 0 ? void 0 : slidesArray.length) > 1 ? (React.createElement(React.Fragment, null,
                React.createElement("div", { ref: containerRef, style: { overflowX: 'visible' }, className: classNames({ container: isWideDesktop }) }, renderCarousel()),
                renderPagination())) : (React.createElement("div", { className: "container" }, renderSlide(slidesArray[0], 0, true)))))));
};
export default CarouselHero;
